/*
 * Copyright (c) 2026 Bolmate B.V.
 */

import './style/productSelection.scss';
import {
    AwesomeIcon,
    ChangeFunction,
    Checkbox,
    CloseButton,
    ElementPosition,
    getSetting,
    Loader,
    OrnamentShape,
    remToPx,
    voidFunction,
    WithBackground
} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';

import React, {KeyboardEventHandler, MouseEventHandler, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import BolProduct from '../../models/bolProductModel';

import {ClauseType, ProductSelectionList, ProductSelectionParam} from '../../util/types';

import {BolmateButton, BolmateInput} from '../controls';

import InfoIcon from '../icon/infoIcon';

import {AppContext} from '../misc/appContext';

import InfiniteList, {ListLoader, RenderRowProps} from '../misc/infiniteList';

interface Props {
    onClose: MouseEventHandler;
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onClearSearch: MouseEventHandler;
    onImportProducts: MouseEventHandler;
    onSelectProduct: (product: BolProduct, clauseType: ClauseType) => MouseEventHandler;
    onSelectAll: (clauseType: ClauseType) => MouseEventHandler;
    onSelectRemaining: ChangeFunction<boolean>;
    onLoadMore: (listType: ProductSelectionList) => ListLoader;
    onSubmit: MouseEventHandler;
    availableProducts: Array<BolProduct>;
    selectedProducts: Array<BolProduct>;
    loadingMore: ProductSelectionParam<boolean>;
    loading: ProductSelectionParam<boolean>;
    parentLoading: boolean;
    search: string;
    total: ProductSelectionParam<number>;
    active: boolean;
    remainingSelected: boolean;
    allSelected: boolean;
    anySelected: boolean;
    importing: boolean;
}

const ITEM_SIZE = remToPx(2.5);

export default function ProductSelection(props: Props) {
    const {t} = useTranslation();
    const {user} = useContext(AppContext).userContext;
    const {
        onClose,
        onChangeSearch,
        onSelectProduct,
        onSelectAll,
        onSelectRemaining,
        onLoadMore,
        onSubmit,
        availableProducts,
        selectedProducts,
        loadingMore,
        loading,
        search,
        total,
        active,
        remainingSelected,
        onEnterSearch,
        allSelected,
        anySelected,
        parentLoading,
        onImportProducts,
        importing,
        onClearSearch
    } = props;

    function renderBody() {
        return (
            <div className='body-container'>
                {parentLoading && <Loader active />}
                {renderSearchBar()}
                {renderGlobalActions()}
                <div className='lists-container'>
                    {renderDisabledOverlay()}
                    <div className='all-buttons'>{renderAllButtons()}</div>
                    {renderSearchLabel()}
                    {renderLists()}
                </div>
            </div>
        );
    }

    function renderSearchBar() {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        const hideSyncButton = !!user && hideSyncForUsers.includes(user?.id);
        const ornaments: Array<OrnamentShape> = [
            {element: <AwesomeIcon name='search' />, placement: ElementPosition.LEFT}
        ];
        if (search.length)
            ornaments.push({
                element: <AwesomeIcon name='circle-xmark' prefix='far' className='click' onClick={onClearSearch} />,
                placement: ElementPosition.RIGHT
            });
        const syncLabel = importing
            ? t('productSelection.importing', 'Bezig met ophalen van producten')
            : t('productSelection.sync', 'Sync met Bol');
        return (
            <div className='search-bar'>
                <BolmateInput
                    onChange={onChangeSearch}
                    value={search}
                    label={t('productSelection.searchProduct', 'Zoek een product')}
                    id='product-select-search'
                    inputProps={{placeholder: t('productSelection.searchPlaceholder', 'EAN of productnaam')}}
                    ornaments={ornaments}
                    onEnter={onEnterSearch}
                    autoFocus
                />
                {!hideSyncButton && (
                    <BolmateButton
                        onClick={onImportProducts}
                        label={syncLabel}
                        disabled={importing}
                        secondary
                        ornaments={{
                            element: importing ? (
                                <AwesomeIcon name='rotate' className='spin' />
                            ) : (
                                <AwesomeIcon name='rotate' />
                            )
                        }}
                    />
                )}
            </div>
        );
    }

    function renderGlobalActions() {
        return (
            <div className='global-actions'>
                <div className='with-info'>
                    <Checkbox
                        onCheck={onSelectRemaining}
                        checked={remainingSelected}
                        label={t('productSelection.selectRemaining', 'Resterende producten selecteren')}
                    />
                    <div className='info-container'>
                        <InfoIcon />
                        <p className='info-text'>
                            {t(
                                'productSelection.selectRemainingInfo',
                                'Met deze optie worden automatisch alle producten geselecteerd waarvoor nog geen andere mail ingesteld is.'
                            )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    function renderSearchLabel() {
        if (!search.length) return null;
        return (
            <div className='search-label-container' onClick={onClearSearch}>
                <span>
                    {t('productSelection.searchedFor', 'Gezocht op')} "{search}"
                </span>
                <AwesomeIcon name='circle-xmark' prefix='far' />
            </div>
        );
    }

    function renderDisabledOverlay() {
        if (!remainingSelected) return null;
        return (
            <div className='overlay'>
                <div className='disabled-text'>
                    <label className='disabled-title'>
                        {t('productSelection.remainingSelected', 'Resterende producten geselecteerd')}
                    </label>
                    <label>
                        {t(
                            'productSelection.remainingSelectedInfo',
                            'Linksboven het vakje "Resterende producten selecteren" om resterende/geen producten te selecteren'
                        )}
                    </label>
                </div>
            </div>
        );
    }

    function renderAllButtons() {
        const icon = <Icon icon='mdi:arrow' className='arrow-icon' />;
        return (
            <>
                <BolmateButton
                    onClick={onSelectAll('exclude')}
                    label={t('productSelection.deselectAll', 'Alles deselecteren')}
                    ornaments={{element: icon, placement: ElementPosition.LEFT}}
                    disabled={!anySelected}
                    className='btn-all selected'
                />
                <BolmateButton
                    onClick={onSelectAll('include')}
                    label={t('productSelection.selectAll', 'Alles selecteren')}
                    ornaments={{element: icon, placement: ElementPosition.RIGHT}}
                    disabled={allSelected}
                    className='btn-all'
                />
            </>
        );
    }

    function renderLists() {
        const showTotals = total.selected > -1 && total.available > -1;
        return (
            <div className='product-lists'>
                <label className='list-title'>
                    {t('productSelection.availableProducts', 'Beschikbare producten')}
                    {!!search.length && ` ${t('productSelection.withSearchTerm', 'met zoekterm')} '${search}' `}
                    {showTotals && ` (${total.available})`}
                </label>
                <div className='divider' />
                <label className='list-title'>
                    {t('productSelection.selectedProducts', 'Geselecteerde producten')}
                    {!!search && ` ${t('productSelection.withSearchTerm', 'met zoekterm')} '${search}' `}
                    {showTotals && ` (${total.selected})`}
                </label>
                {renderAvailableList()}
                {renderSelectedList()}
            </div>
        );
    }

    function renderAvailableList() {
        const hasMore = availableProducts.length < total.available;
        return (
            <div>
                <InfiniteList
                    onLoadMore={onLoadMore('available')}
                    renderRow={renderListRow('include')}
                    hasMore={hasMore}
                    isLoadingMore={loadingMore.available}
                    itemCount={availableProducts.length}
                    itemSize={ITEM_SIZE}
                    stickyIndexes={[0]}
                    renderStickyRow={renderListHead}
                />
            </div>
        );
    }

    function renderSelectedList() {
        const hasMore = selectedProducts.length < total.selected;
        return (
            <div>
                <InfiniteList
                    onLoadMore={onLoadMore('selected')}
                    renderRow={renderListRow('exclude')}
                    hasMore={hasMore}
                    isLoadingMore={loadingMore.selected}
                    itemCount={selectedProducts.length}
                    itemSize={ITEM_SIZE}
                    stickyIndexes={[0]}
                    renderStickyRow={renderListHead}
                />
            </div>
        );
    }

    function renderListHead(props: RenderRowProps) {
        return (
            <div key={props.index} style={props.style} className='default-table-row list-row head'>
                <div />
                <label>{t('productSelection.ean', 'EAN')}</label>
                <label>{t('productSelection.productName', 'Productnaam')}</label>
            </div>
        );
    }

    function renderListRow(clauseType: ClauseType) {
        return function (props: RenderRowProps) {
            const {style, index, isLoaded} = props;
            if (!isLoaded)
                return (
                    <div style={style} className='default-table-row list-row'>
                        <div />
                        <label>{t('productSelection.loadingProduct', 'Product laden...')}</label>
                        <label />
                    </div>
                );
            const isSelected = clauseType === 'exclude';
            const useList: Array<BolProduct> = isSelected ? selectedProducts : availableProducts;
            const product = useList[index];
            if (!product) return <div />;
            return (
                <div
                    key={index}
                    style={style}
                    className='default-table-row list-row'
                    onClick={onSelectProduct(product, clauseType)}
                >
                    <Checkbox onCheck={voidFunction} checked={isSelected} noRipple />
                    <label>{product.ean}</label>
                    <label>{product.actualTitle}</label>
                </div>
            );
        };
    }

    const submitText = search.length ? t('productSelection.next', 'Volgende') : t('productSelection.done', 'Klaar');
    const submit = search.length ? onClearSearch : onSubmit;
    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='default-popup product-select'>
                <div className='header-container'>
                    <label className='header-title'>{t('productSelection.title', 'Producten selecteren')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <BolmateButton
                        onClick={submit}
                        label={submitText}
                        disabled={loading.available || loading.selected}
                        secondary
                    />
                </div>
            </div>
        </WithBackground>
    );
}

ProductSelection.displayName = 'ProductSelection';
